import {LOCALE_CATEGORIES} from "@/assets/locales/categories.ts";
import {saveCoacheesListQuery} from "@/domains/-session/functions/coacheesListQuery";
import {customAsyncComponent} from "@/functions/customAsyncComponent";
import {GetWorkspaceCoachees, JourneyName, WorkspaceId} from "@/sdk.ts";
import {LocationQueryRaw, RouteLocationRaw, Router, RouteRecordRaw} from "vue-router";

export namespace CoacheeListRoute {
  export function record(): RouteRecordRaw {
    return {
      name: 'coachee_list',
      path: '/workspace/:workspaceId/coachee',
      component: customAsyncComponent(async () => import('./views/ClientListView.vue')),
      meta: {
        hasNav: true,
        requiresAuth: true,
        requiresCoach: true,
        localeCategories: [
          LOCALE_CATEGORIES.JOURNEY,
          LOCALE_CATEGORIES.COACHEES,
          LOCALE_CATEGORIES.PAGINATION
        ],
        pageHeader: {
          showHome: false,
          showTitle: true,
          title: 'Welcome back !',
          breadcrumb: []
        }
      },
    }
  }

  export function location(workspaceId: number, query?: GetWorkspaceCoachees.Query, reset?: boolean): RouteLocationRaw {
    if (reset) return {name: 'coachee_list', params: {workspaceId}, query: {reset: 1}};
    return {name: 'coachee_list', params: {workspaceId}, query: query as LocationQueryRaw | undefined};
  }

  export function getParams(router: Router): { workspaceId: WorkspaceId; } {
    return {
      workspaceId: parseInt(router.currentRoute.value.params.workspaceId as string, 10),
    }
  }

  export function getQuery(router: Router, defaultQuery: GetWorkspaceCoachees.Query): GetWorkspaceCoachees.Query {
    const route = router.currentRoute.value;

    const query: Partial<GetWorkspaceCoachees.Query> = {};

    if (route.query.page && typeof route.query.page === "string") {
      query.page = parseInt(route.query.page);
    } else {
      query.page = defaultQuery.page;
    }

    if (route.query.page_size && typeof route.query.page_size === "string") {
      query.page_size = parseInt(route.query.page_size);
    } else {
      query.page_size = defaultQuery.page_size;
    }
    if (route.query.journey_name) {
      if (typeof route.query.journey_name === "string") {
        query.journey_name = [route.query.journey_name as JourneyName];
      } else if (route.query.organization_pid instanceof Array) {
        query.journey_name = route.query.journey_name.filter(e => typeof e === "string") as JourneyName[];
      }
    } else {
      query.journey_name = defaultQuery.journey_name;
    }
    if (route.query.organization_pid) {
      if (typeof route.query.organization_pid === "string") {
        query.organization_pid = [route.query.organization_pid];
      } else {
        query.organization_pid = route.query.organization_pid.filter(e => typeof e === "string") as string[];
      }
    } else {
      query.organization_pid = defaultQuery.organization_pid;
    }
    if (route.query.promotion_pid) {
      if (typeof route.query.promotion_pid === "string") {
        query.promotion_pid = [route.query.promotion_pid];
      } else {
        query.promotion_pid = route.query.promotion_pid.filter(e => typeof e === "string") as string[];
      }
    } else {
      query.promotion_pid = defaultQuery.promotion_pid;
    }
    if (route.query.search && typeof route.query.search === "string") {
      query.search = route.query.search;
    } else {
      query.search = defaultQuery.search;
    }
    if (route.query.sort_by && typeof route.query.sort_by === "string") {
      query.sort_by = route.query.sort_by as GetWorkspaceCoachees.SortBy;
    } else {
      query.sort_by = defaultQuery.sort_by;
    }

    return query as GetWorkspaceCoachees.Query;
  }
}

export function getClientListRouteRecord(): RouteRecordRaw {
  return {
    name: 'coachee_list',
    path: '/workspace/:workspaceId/coachee',
    component: customAsyncComponent(async () => import('./views/ClientListView.vue')),
    meta: {
      hasNav: true,
      requiresAuth: true,
      requiresCoach: true,
      localeCategories: [
        LOCALE_CATEGORIES.JOURNEY,
        LOCALE_CATEGORIES.COACHEES,
        LOCALE_CATEGORIES.PAGINATION
      ],
      pageHeader: {
        showHome: false,
        showTitle: true,
        title: 'Welcome back !',
        breadcrumb: []
      }
    },
  }
}

export function getClientListRouteLocation(workspaceId: number, query?: GetWorkspaceCoachees.Query, reset?: boolean): RouteLocationRaw {
  if (reset) return {name: 'coachee_list', params: {workspaceId}, query: {reset: 1}};
  return {name: 'coachee_list', params: {workspaceId}, query: query as LocationQueryRaw | undefined};
}

export function getClientListRouteParams(router: Router): { workspaceId: WorkspaceId; } {
  return {
    workspaceId: parseInt(router.currentRoute.value.params.workspaceId as string, 10),
  }
}

export function getClientListRouteQuery(router: Router, defaultQuery: GetWorkspaceCoachees.Query): GetWorkspaceCoachees.Query {
  const route = router.currentRoute.value;

  const query: Partial<GetWorkspaceCoachees.Query> = {};

  if (route.query.page && typeof route.query.page === "string") {
    query.page = parseInt(route.query.page);
  } else {
    query.page = defaultQuery.page;
  }

  if (route.query.page_size && typeof route.query.page_size === "string") {
    query.page_size = parseInt(route.query.page_size);
  } else {
    query.page_size = defaultQuery.page_size;
  }
  if (route.query.journey_name) {
    if (typeof route.query.journey_name === "string") {
      query.journey_name = [route.query.journey_name as JourneyName];
    } else if (route.query.organization_pid instanceof Array) {
      query.journey_name = route.query.journey_name.filter(e => typeof e === "string") as JourneyName[];
    }
  } else {
    query.journey_name = defaultQuery.journey_name;
  }
  if (route.query.organization_pid) {
    if (typeof route.query.organization_pid === "string") {
      query.organization_pid = [route.query.organization_pid];
    } else {
      query.organization_pid = route.query.organization_pid.filter(e => typeof e === "string") as string[];
    }
  } else {
    query.organization_pid = defaultQuery.organization_pid;
  }
  if (route.query.promotion_pid) {
    if (typeof route.query.promotion_pid === "string") {
      query.promotion_pid = [route.query.promotion_pid];
    } else {
      query.promotion_pid = route.query.promotion_pid.filter(e => typeof e === "string") as string[];
    }
  } else {
    query.promotion_pid = defaultQuery.promotion_pid;
  }
  if (route.query.search && typeof route.query.search === "string") {
    query.search = route.query.search;
  } else {
    query.search = defaultQuery.search;
  }
  if (route.query.sort_by && typeof route.query.sort_by === "string") {
    query.sort_by = route.query.sort_by as GetWorkspaceCoachees.SortBy;
  } else {
    query.sort_by = defaultQuery.sort_by;
  }

  return query as GetWorkspaceCoachees.Query;
}
